import { useEffect } from 'react'
import { getSessionToken, getStoredUser, setSessionToken, setStoredUser } from './authStorage'
import { getSession } from './authApi'
import { performLogout } from './performLogout'
import { startTokenRefreshScheduler, stopTokenRefreshScheduler } from './tokenRefresh'
import type { ReactNode } from 'react'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

const SIGN_IN_PATH = '/auth'

/** The server rejected the session: same path as any other forced logout. */
const signOutExpiredSession = async (): Promise<void> => {
  logger.logEvent(LogCategory.AUTH_SESSION, 'session.expired.forcing_logout')
  await performLogout()
  if (window.location.pathname !== SIGN_IN_PATH) {
    window.location.href = SIGN_IN_PATH
  }
}

/**
 * Sessions restored from storage without a profile (e.g. the token survived in
 * the IndexedDB mirror but the profile didn't) get it from /auth/get-session.
 * Best effort: offline it just stays on the JWT-claims fallback.
 */
const loadMissingUser = async (): Promise<void> => {
  const sessionToken = getSessionToken()
  if (!sessionToken || getStoredUser()) return
  try {
    const result = await getSession(sessionToken)
    if (getSessionToken() !== sessionToken) return
    if (result.sessionToken) setSessionToken(result.sessionToken)
    if (result.data) setStoredUser(result.data)
  } catch (error) {
    logger.logEvent(LogCategory.AUTH_SESSION, 'session.user_load_failed', {
      message: error instanceof Error ? error.message : 'unknown',
    })
  }
}

/**
 * Owns the auth lifecycle for the page: proactive JWT refresh, cross-tab
 * token sync, and the forced logout when the server revokes the session.
 * Replaces react-oidc-context's AuthProvider. State itself lives in
 * authStorage and is read with `useAuth()`.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    startTokenRefreshScheduler({ onSessionExpired: signOutExpiredSession })
    void loadMissingUser()
    return () => stopTokenRefreshScheduler()
  }, [])

  return <>{children}</>
}
